import { http } from '@/api/http'

/** One person in the admin console now (GET /presence/online): their tabs folded into one row. */
export type OnlineUser = {
  profile_id: string
  display_name: string
  avatar_url: string
  role_name_en: string
  role_name_ru: string
  role_name_uz: string
  branch_name_en: string
  branch_name_ru: string
  branch_name_uz: string
  /** active: some tab had input in the last 5 min; idle: none did. */
  status: 'active' | 'idle'
  /** Admin route of the tab used most recently. */
  path: string
  page_since: string
  idle_for_seconds: number
  tabs: number
  last_seen_at: string
}

export type OnlineUsers = { items: OnlineUser[]; active: number; idle: number }

export type ActivityVisit = {
  id: string
  tab_id: string
  path: string
  started_at: string
  last_seen_at: string
  /** Null while the visit is open. */
  ended_at: string | null
  active_seconds: number
  idle_seconds: number
}

/** Totals of one Asia/Tashkent day; parallel tabs count once. */
export type ActivityDay = {
  date: string
  online_seconds: number
  active_seconds: number
  idle_seconds: number
  first_seen_at: string | null
  last_seen_at: string | null
}

export type UserActivity = {
  profile_id: string
  display_name: string
  from: string
  to: string
  /** Null when the person is offline. */
  online: OnlineUser | null
  days: ActivityDay[]
  /** Newest first. */
  visits: ActivityVisit[]
}

/** Admin console presence («Онлайн»): every tab pings; users.activity.read reads. */
export const presenceService = {
  async heartbeat(body: { tab_id: string; path: string; idle_seconds: number }) {
    await http.post('/presence/heartbeat', body)
  },
  /** Sent while the tab unloads: keepalive lets the request outlive the page. */
  async leave(body: { tab_id: string; idle_seconds: number }) {
    await http.post('/presence/leave', body, { adapter: 'fetch', fetchOptions: { keepalive: true } })
  },
  async online(signal?: AbortSignal) {
    const { data } = await http.get<OnlineUsers>('/presence/online', { signal })
    return { ...data, items: data.items ?? [] }
  },
  async activity(profileId: string, params: { from?: string; to?: string } = {}, signal?: AbortSignal) {
    const { data } = await http.get<UserActivity>(`/presence/users/${profileId}/activity`, { params, signal })
    return { ...data, days: data.days ?? [], visits: data.visits ?? [] }
  },
}
